import { Controller } from "@hotwired/stimulus";

const LABEL_SELECTOR = ".shared--searchable-dropdown-component__label";
const ROW_CLUSTER_TOLERANCE_PX = 4;
const BINARY_SEARCH_PRECISION_PX = 8;

/**
 * Clips the AG-Grid toolbar's nav-dropdown label(s) to the widest width that
 * still minimises the toolbar's wrapped-row count -- but only when clipping
 * actually helps.
 *
 * Static CSS breakpoints can't solve this: "is there room" depends on the
 * sum of every sibling's width, which varies per page and per Basic/Advanced
 * mode, not on viewport width. Pure flexbox can't either: flex-wrap assigns
 * items to lines using their hypothetical (flex-basis) size before shrinking
 * resolves, so a 0% basis is needed to fix line-assignment -- but flex-grow
 * then reinflates the item whenever it's alone on a line with slack, which
 * is exactly when it's crowding the search box out. Those two requirements
 * fight over the same CSS property.
 *
 * Attaches to the toolbar bar element itself. Below the CSS "stacked"
 * breakpoint (bar's --ag-toolbar-stacked custom property), CSS alone owns
 * the layout and this controller stands down.
 */
export default class extends Controller {
  static values = {
    minLabelWidth: { type: Number, default: 72 },
  };

  declare readonly minLabelWidthValue: number;

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private pendingFrame: number | null = null;
  private lastWidth = -1;
  private applying = false;

  connect() {
    const bar = this.barElement;

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(bar);

    this.mutationObserver = new MutationObserver(() => {
      if (this.applying) return;
      this.scheduleRecompute();
    });
    this.mutationObserver.observe(bar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "hidden", "class"],
    });

    this.scheduleRecompute();
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
  }

  private get barElement(): HTMLElement {
    return this.element as HTMLElement;
  }

  // Only width changes are layout-relevant here; our own clipping changes
  // the bar's HEIGHT (rows collapse), which would otherwise re-fire this
  // observer forever.
  private handleResize() {
    const width = this.barElement.clientWidth;
    if (width === this.lastWidth) return;

    this.lastWidth = width;
    this.scheduleRecompute();
  }

  // Coalesces every trigger (resize + mutation) through a single rAF so the
  // binary search's forced-layout reads run at most once per frame.
  private scheduleRecompute() {
    if (this.pendingFrame !== null) cancelAnimationFrame(this.pendingFrame);

    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.recompute();
    });
  }

  private recompute() {
    const bar = this.barElement;
    const labels = this.managedLabels(bar);

    this.applyMaxWidth(labels, null);
    if (this.isStacked(bar) || labels.length === 0) return;

    const items = this.effectiveFlexItems(bar);
    const baseRows = this.countRows(items);
    if (baseRows <= 1) return;

    // offsetWidth, not scrollWidth: it respects the stylesheet's max-width cap,
    // so we never widen a label past what CSS alone would have rendered.
    const naturalWidth = Math.max(...labels.map((label) => label.offsetWidth));

    this.applyMaxWidth(labels, this.minLabelWidthValue);
    const minRows = this.countRows(items);
    if (minRows >= baseRows) {
      // Minimum-width clipping didn't reduce the row count, so the wrap has some other
      // cause -- clipping would be pointless.
      this.applyMaxWidth(labels, null);
      return;
    }

    const bestWidth = this.largestWidthForRowCount(
      labels,
      items,
      minRows,
      this.minLabelWidthValue,
      naturalWidth
    );
    this.applyMaxWidth(labels, bestWidth);
  }

  // Binary-searches the largest max-width in [low, high] that still yields
  // `targetRows` wrapped rows, to sub-8px precision.
  private largestWidthForRowCount(
    labels: HTMLElement[],
    items: HTMLElement[],
    targetRows: number,
    low: number,
    high: number
  ): number {
    let lo = low;
    let hi = high;
    let best = low;

    while (hi - lo >= BINARY_SEARCH_PRECISION_PX) {
      const mid = Math.round((lo + hi) / 2);
      this.applyMaxWidth(labels, mid);

      if (this.countRows(items) === targetRows) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }

    return best;
  }

  private applyMaxWidth(labels: HTMLElement[], width: number | null) {
    this.applying = true;
    labels.forEach((label) => {
      if (width === null) {
        label.style.removeProperty("max-width");
      } else {
        label.style.maxWidth = `${width}px`;
      }
    });
    // Drain records generated by the writes above so the mutation observer's
    // callback never fires for changes we caused ourselves.
    this.mutationObserver?.takeRecords();
    this.applying = false;
  }

  private managedLabels(bar: HTMLElement): HTMLElement[] {
    return Array.from(bar.querySelectorAll<HTMLElement>(LABEL_SELECTOR));
  }

  private isStacked(bar: HTMLElement): boolean {
    return getComputedStyle(bar).getPropertyValue("--ag-toolbar-stacked").trim() === "1";
  }

  private isRendered(el: HTMLElement): boolean {
    if (getComputedStyle(el).display === "none" || el.hidden) return false;

    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // The bar's real flex participants: `display: contents` wrappers are transparent to
  // flex layout, so their children -- not the wrapper -- are the actual flex items.
  private effectiveFlexItems(bar: HTMLElement): HTMLElement[] {
    const items: HTMLElement[] = [];

    const visit = (parent: HTMLElement) => {
      Array.from(parent.children).forEach((child) => {
        const el = child as HTMLElement;
        // Must precede the isRendered check: a display:contents element generates
        // no box, so its rect is 0x0 and isRendered would discard it unrecursed.
        if (getComputedStyle(el).display === "contents") {
          visit(el);
        } else if (this.isRendered(el)) {
          items.push(el);
        }
      });
    };

    visit(bar);
    return items;
  }

  // Clusters items into flex lines by vertical centre -- align-items: center means every
  // item on a line shares a centre regardless of differing heights.
  private countRows(items: HTMLElement[]): number {
    const centers = items
      .map((item) => {
        const rect = item.getBoundingClientRect();
        return rect.top + rect.height / 2;
      })
      .sort((a, b) => a - b);

    let rows = 0;
    let lastCenter: number | null = null;
    centers.forEach((center) => {
      if (lastCenter === null || Math.abs(center - lastCenter) > ROW_CLUSTER_TOLERANCE_PX) {
        rows += 1;
      }
      lastCenter = center;
    });

    return rows;
  }
}
